<template>
    <div :id="`segmentConditionRow${id}`" class="segment-condition">
        <div class="column first">
            <label class="font-4">{{ isFirst ? 'Condition' : 'And' }}</label>
        </div>

        <ErrorField class="column" type="required" field="Category" :condition="!errors || errors.nameId">
            <Dropdown
                class="property-field"
                :uniqueId="`segmentConditionNameId${id}`"
                :selectedId="nameId"
                :options="options"
                defaultText="Select Property"
                :searchEnabled="false"
                @dropdownSelectedItemChanged="$emit('update:nameId', $event.id)"
                height="34"
            />
        </ErrorField>

        <!-- Custom Segment -->
        <StringField
            v-if="nameId === segmentConsts.PROPERTY_IDS.customSegment"
            :value="value"
            :errors="errors"
            comparatorHidden
            @update:value="handleCustomSegmentValueChange">
            <template v-slot:afterInput>
                <Tooltip
                    class="custom-segment-tooltip"
                    :container="false"
                    text="Enter the identical name of the segment as manifested in your API">
                    <IconBase iconName="infoTooltipIcon" :iconColor="consts.COLORS.LIGHT_700" width="12" height="12" />
                </Tooltip>
            </template>
        </StringField>

        <!-- Custom Property -->
        <CustomField
            v-if="nameId === segmentConsts.PROPERTY_IDS.customProperty"
            :value="value"
            :comparator="comparator"
            :customName="customName"
            :customType="customType"
            :metadata="metadata.customProperties || {}"
            :errors="errors"
            v-on="commonFieldListeners"
            @update:customType="$emit('update:customType', $event)"
            @update:customName="$emit('update:customName', $event)"
        />

        <!-- Condition Properties -->
        <template v-else>

            <IntegerField
                v-if="propMeta.type === segmentConsts.FIELD_TYPES.Integer"
                v-bind="commonFieldProps"
                v-on="commonFieldListeners">
                <template v-slot:afterInput>
                    <span class="label-after-input label-after-input-years font-4">
                        {{ nameId === segmentConsts.PROPERTY_IDS.age ? 'Years' : '' }}
                    </span>
                </template>
            </IntegerField>

            <StringField
                v-if="propMeta.type === segmentConsts.FIELD_TYPES.String"
                v-bind="commonFieldProps"
                v-on="commonFieldListeners"
                class="string-field">
                <template v-slot:afterInput>
                    <Tooltip :text="segmentConsts.WILDCARD_TOOLTIP_TEXT" placement="right" :container="false">
                        <p class="field-tip">
                            <span class="font-6">This property supports wildcards</span>
                            <IconBase
                                iconName="infoTooltipIcon"
                                class="segment-condition-tooltip-icon"
                                :iconColor="consts.COLORS.LIGHT_700"
                                width="12"
                                height="12"
                            />
                            <a :href="links.segmentsSetup" class="font-5">Read More</a>
                        </p>
                    </Tooltip>
                </template>
            </StringField>

            <VersionField
                v-if="propMeta.type === segmentConsts.FIELD_TYPES.Version"
                v-bind="commonFieldProps"
                v-on="commonFieldListeners"
            />

            <EnumField
                v-if="propMeta.type === segmentConsts.FIELD_TYPES.Enum"
                v-bind="commonFieldProps"
                v-on="commonFieldListeners"
                :options="makeOptionsFromArray(propMeta.options)"
                :placeholder="'Select ' + propMeta.name"
            />

            <DateField
                v-if="propMeta.type === segmentConsts.FIELD_TYPES.Date"
                v-bind="commonFieldProps"
                v-on="commonFieldListeners"
            />

            <BooleanField
                v-if="propMeta.type === segmentConsts.FIELD_TYPES.Boolean"
                :value="value"
                :errors="errors"
                v-on="commonFieldListeners">
                <template v-slot:afterInput>
                    <p class="field-tip supported-version-tip font-6" v-if="supportedFromSDK701">
                        Supported from SDK 7.0.1
                    </p>
                </template>
            </BooleanField>

            <CountryField
                v-if="propMeta.type === segmentConsts.FIELD_TYPES.Array
                   && nameId === segmentConsts.PROPERTY_IDS.country"
                v-bind="commonFieldProps"
                v-on="commonFieldListeners"
                :countriesData="countriesData"
            />
        </template>
    </div>
</template>

<script>
import Dropdown from 'components/dropdowns/Dropdown.vue';
import Tooltip from 'components/Tooltip/Tooltip.vue';
import IconBase from 'components/IconBase.vue';
import consts from 'services/metaData/consts';
import links from 'services/metaData/links';
import ErrorField from 'components/errorField/ErrorField.vue';
import IntegerField from './fields/IntegerField.vue';
import StringField from './fields/StringField.vue';
import VersionField from './fields/VersionField.vue';
import EnumField from './fields/EnumField.vue';
import DateField from './fields/DateField.vue';
import BooleanField from './fields/BooleanField.vue';
import CountryField from './fields/CountryField.vue';
import CustomField from './fields/CustomField.vue';
import * as segmentConsts from './consts';
import { PROPERTY_IDS } from './consts';

export default {
    name: 'SegmentCondition',
    components: {
        ErrorField,
        CustomField,
        CountryField,
        BooleanField,
        DateField,
        EnumField,
        VersionField,
        StringField,
        IntegerField,
        Dropdown,
        Tooltip,
        IconBase
    },
    props: {
        isFirst: {
            type: Boolean,
            default: false
        },
        metadata: {
            type: Object,
            required: true
        },
        allowCustomSegment: {
            type: Boolean,
            default: true
        },
        nameId: Number,
        id: Number,
        comparator: String,
        value: [Number, String, Array, Boolean],
        customType: String,
        customName: String,
        countriesData: Object,
        errors: Object,
        isAndroid: Boolean,
        allowCustomProperties: Boolean
    },
    computed: {
        supportedFromSDK701() {
            if (!this.nameId) return undefined;
            const supportedNameIds = [segmentConsts.PROPERTY_IDS.idfa, segmentConsts.PROPERTY_IDS.coppa];

            return supportedNameIds.includes(this.nameId);
        },
        metadataProps() {
            return this.metadata.defaultProperties || [];
        },
        propMeta() {
            return this.metadataProps.find((prop) => prop.nameId === this.nameId) || {};
        },
        options() {
            const optionsToFilter = this.isAndroid
                ? [PROPERTY_IDS.idfa, PROPERTY_IDS.osVersion] : [PROPERTY_IDS.apiLevel];
            const properties = [
                ...this.metadataProps.filter((prop) => !optionsToFilter.includes(prop.nameId)),
                { nameId: segmentConsts.PROPERTY_IDS.customProperty, name: 'Custom Property' }
            ];
            return properties.map((prop) => {
                const isCustomSegmentDisabled = prop.nameId === segmentConsts.PROPERTY_IDS.customSegment
                    && !this.allowCustomSegment;
                const isCustomPropertyDisabled = prop.nameId === segmentConsts.PROPERTY_IDS.customProperty
                    && !this.allowCustomProperties;

                return {
                    id: prop.nameId,
                    displayText: prop.name,
                    disabled: isCustomSegmentDisabled || isCustomPropertyDisabled
                };
            });
        },
        commonFieldProps() {
            return {
                value: this.value,
                comparator: this.comparator,
                errors: this.errors,
                id: this.id,
                comparatorOptions: this.makeOptionsFromArray(this.propMeta.comparators)
            };
        },
        commonFieldListeners() {
            return {
                'update:comparator': this.handleComparatorChange,
                'update:value': this.handleValueChange
            };
        },
        consts: () => consts,
        links: () => links,
        segmentConsts: () => segmentConsts
    },
    methods: {
        makeOptionsFromArray(arr) {
            return arr ? arr.map((el) => ({ id: el, displayText: el })) : [];
        },
        handleValueChange(val) {
            this.$emit('update:value', val);
        },
        handleComparatorChange(val) {
            this.$emit('update:comparator', val);
        },
        handleCustomSegmentValueChange(val) {
            this.$emit('update:value', val);
            this.$emit('update:comparator', 'Equals');
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.scss";

.segment-condition {
    width: 100%;
    display: flex;
}

// these are helper styles used in child field components
// (like IntegerField, StringField, EnumField, etc.)
// defined here to not duplicate in each component
/deep/ {
    .column {
        display: flex;
        align-items: flex-start;
        width: 200px;
        padding: 0 10px;
        &.first {
            padding-top: 6px;
            padding-left: 0;
            width: 68px;
        }
    }
    .stretch {
        display: flex;
        flex: 1;
        align-items: flex-start;
        width: auto;
    }
    .center-vertically {
        align-self: center;
        align-items: center;
    }
}

.field-tip {
    max-width: 160px;
    margin: 0;
    color: $light-800;
    line-height: 16px;
    padding-left: 10px;
    a {
        color: $primary;
        text-decoration: none;
    }
}

.supported-version-tip {
    line-height: 32px;
}

.label-after-input {
    margin-left: 19px;
}

// Tooltip styles
/deep/ {
    .tooltip {
        width: 200px;
    }
    .string-field .tooltip {
        width: 165px;
        hyphens: auto;
    }
}

.segment-condition-tooltip-icon {
    margin-left: 4px;
    margin-right: 4px;
}

.label-after-input-years {
    line-height: 32px;
    margin-left: 10px;
}
</style>
